/**
 * The control bar's glyphs (web-ui ticket 03).
 *
 * The same three rules `packages/ui/src/icons.tsx` sets out, for the same
 * reasons — one 24×24 viewBox so every shape sits on a shared optical centre,
 * `fill="currentColor"` so a button's colour is the one CSS rule that decides
 * what its glyph looks like, and `aria-hidden` because every one of these is
 * inside a button that already carries an `aria-label`.
 *
 * **Web-local rather than shared, and that is a decision rather than an
 * oversight.** Six of these nine have no counterpart on the desktop at all: a tab
 * is the only host with a camera and a microphone it can turn off, and the
 * desktop's window has no full-screen control or overflow menu. The three that do
 * look like the desktop's are the three that deliberately differ — the web's armed
 * record glyph is a *dot* inside a 64px filled circle, where the desktop's is a
 * rounded square inside a bordered 40px one, because the web bar's record button
 * is the visual centre of the whole page and the desktop's is one square in a row
 * of squares. `packages/ui/src/index.ts` exports no icons for exactly this reason:
 * a shape shared between two hosts that want it to look different is a shape that
 * ends up looking wrong on both.
 *
 * The trigger for reconsidering is the one that file names: a glyph nobody can
 * draw out of rectangles, circles and one arc. None of these is.
 */
import { useId } from "react";

/** The shared frame, so nine shapes cannot drift on the viewBox, the fill or the
 * `aria-hidden`. */
function Icon({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      {children}
    </svg>
  );
}

/**
 * The bar through a muted mic or a stopped camera.
 *
 * One shape, used by both off states, so the two cannot end up struck through at
 * different angles. Drawn corner to corner rather than as a short dash: an
 * *active-off* button is already inverted to `--record-deep`, and the slash is
 * what stops it reading as "on and highlighted".
 *
 * `thickness` is the second, wider copy — see {@link Struck}.
 */
function Slash({ thickness = 2.2 }: { thickness?: number }): JSX.Element {
  return (
    <rect
      x="2.4"
      y={12 - thickness / 2}
      width="19.2"
      height={thickness}
      rx={thickness / 2}
      transform="rotate(-45 12 12)"
    />
  );
}

/**
 * A glyph with a bar through it, and a *gap* either side of the bar.
 *
 * The gap is the whole thing. Everything here is one flat `currentColor`, so a
 * slash laid over a filled camera body is the same colour as the body and simply
 * disappears into it — which is what happened: at 48px in the middle of the green
 * room's stage, "Camera and microphone are blocked" was drawn with a glyph
 * indistinguishable from the one above "See yourself before you record". The two
 * states that most need to be told apart at a glance looked identical.
 *
 * So the shape is masked by a wider copy of the slash before the slash itself is
 * drawn on top: the bar reads as cutting *through* the glyph rather than lying on
 * it, at any size and against any background, without either of them having to
 * know what colour the other is.
 *
 * The mask needs an id unique to the document, and both off-glyphs can be on
 * screen at once — hence `useId` rather than a constant.
 */
function Struck({ children }: { children: React.ReactNode }): JSX.Element {
  const id = `struck-${useId()}`;
  return (
    <Icon>
      <mask id={id}>
        <rect width="24" height="24" fill="white" />
        <g fill="black">
          <Slash thickness={5.6} />
        </g>
      </mask>
      <g mask={`url(#${id})`}>{children}</g>
      <Slash />
    </Icon>
  );
}

/** Microphone: a capsule in a cradle. The cradle is a filled crescent rather than
 * a stroked arc, because nothing in either icon set carries a stroke — a stroke
 * width is a second thing that would have to be kept in step with the glyph
 * size. */
function MicShape(): JSX.Element {
  return (
    <>
      <rect x="9" y="2.6" width="6" height="11.4" rx="3" />
      <path d="M6 11.4a6 6 0 0 0 12 0h-1.9a4.1 4.1 0 0 1-8.2 0z" />
      <rect x="11.1" y="17.2" width="1.8" height="3.2" rx="0.9" />
      <rect x="7.4" y="19.6" width="9.2" height="1.9" rx="0.95" />
    </>
  );
}

export function MicIcon(): JSX.Element {
  return (
    <Icon>
      <MicShape />
    </Icon>
  );
}

export function MicOffIcon(): JSX.Element {
  return (
    <Struck>
      <MicShape />
    </Struck>
  );
}

/** Camera: the body and the prism, which is the shape a video camera is read as
 * rather than a photographic one — this records footage, and a shutter glyph
 * would say it took a still. */
function CameraShape(): JSX.Element {
  return (
    <>
      <rect x="2" y="6" width="13.4" height="12" rx="2.6" />
      <polygon points="17,11 22,7.4 22,16.6" />
    </>
  );
}

export function CameraIcon(): JSX.Element {
  return (
    <Icon>
      <CameraShape />
    </Icon>
  );
}

export function CameraOffIcon(): JSX.Element {
  return (
    <Struck>
      <CameraShape />
    </Struck>
  );
}

/** Full screen: four corner brackets, which is the `⛶` this replaces. Brackets
 * rather than arrows, because the button toggles — a pair of arrows pointing
 * outwards would be wrong for half of its life. */
export function FullScreenIcon(): JSX.Element {
  const bracket = (x: number, y: number, flipX: boolean, flipY: boolean) => (
    <g transform={`translate(${x} ${y}) scale(${flipX ? -1 : 1} ${flipY ? -1 : 1})`}>
      <rect width="7" height="2.2" rx="1.1" />
      <rect width="2.2" height="7" rx="1.1" />
    </g>
  );
  return (
    <Icon>
      {bracket(3, 3, false, false)}
      {bracket(21, 3, true, false)}
      {bracket(3, 21, false, true)}
      {bracket(21, 21, true, true)}
    </Icon>
  );
}

/** More: the `⋯`, as three dots on the optical centre line. */
export function MoreIcon(): JSX.Element {
  return (
    <Icon>
      <circle cx="5.4" cy="12" r="1.9" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="18.6" cy="12" r="1.9" />
    </Icon>
  );
}

/**
 * The armed record button's dot.
 *
 * A circle and not the desktop's rounded square. The desktop's argument for the
 * square was that it is the same shape as its stop, so the two read as *armed* and
 * *rolling* about one take — and that argument is about a row of same-sized
 * squares. Here the two are the same 64px circle in the same place, so shape is
 * the only thing left to tell them apart, and the dot is the convention every
 * camera in the world uses for it.
 */
export function RecordDotIcon(): JSX.Element {
  return (
    <Icon>
      <circle cx="12" cy="12" r="10" />
    </Icon>
  );
}

/** Stop: the rounded square, filling the same footprint the dot does so the
 * button's contents do not change size when a take starts. */
export function StopSquareIcon(): JSX.Element {
  return (
    <Icon>
      <rect x="2" y="2" width="20" height="20" rx="5" />
    </Icon>
  );
}

/** Pause: two bars, on the footprint the desktop's uses. */
export function PauseIcon(): JSX.Element {
  return (
    <Icon>
      <rect x="5" y="4" width="5" height="16" rx="1.5" />
      <rect x="14" y="4" width="5" height="16" rx="1.5" />
    </Icon>
  );
}

/**
 * Resume: a play triangle.
 *
 * The one place this set departs from `transportButtons`' own vocabulary, which
 * calls the resume glyph `record`. On the desktop that is right — one button
 * starts the take and then holds it, so the shape that means "go" is the record
 * shape. Here the record button is a separate 64px circle two positions away, and
 * a second red-adjacent dot beside it is the one confusion this bar cannot afford.
 */
export function ResumeIcon(): JSX.Element {
  return (
    <Icon>
      <path d="M7.4 4.6a1.2 1.2 0 0 1 1.83-1.02l11.2 7.4a1.2 1.2 0 0 1 0 2.04l-11.2 7.4A1.2 1.2 0 0 1 7.4 19.4z" />
    </Icon>
  );
}
